// **Η αναφορά χρήστη (`@όνομα`), ως ΕΝΑΣ κανόνας.** ΛΑ-22, Απόφαση D44.
//
// Το άρθρωμα είναι **καθαρές συναρτήσεις**: δεν αγγίζει τη βάση, δεν γνωρίζει
// χρήστες, δεν γνωρίζει συλλογές. Απαντά σε **ένα** ερώτημα — «ποια ονόματα
// αναφέρει αυτό το κείμενο, και πού ακριβώς;» — και το απαντά με τον ίδιο τρόπο
// στον διακομιστή και στον πελάτη.
//
// ⚠ **ΓΙΑΤΙ ΕΔΩ, ΚΑΙ ΟΧΙ ΔΥΟ ΦΟΡΕΣ.** Ο πελάτης χρειάζεται τον κανόνα για δύο
// πράγματα: να προσφέρει ονόματα καθώς πληκτρολογεί ο χρήστης, και να επισημάνει
// τις αναφορές στο αποδοσμένο κείμενο. Ο διακομιστής τον χρειάζεται για να
// **αποφασίσει ποιος ειδοποιείται**. Δύο υλοποιήσεις σημαίνουν ότι ο επιλογέας
// συμπληρώνει κάτι που ο διακομιστής δεν μετράει ως αναφορά — και η απόκλιση δεν
// παράγει σφάλμα πουθενά: παράγει **ειδοποίηση που δεν φτάνει**, δηλαδή τη
// σιωπηλή αποτυχία που η ΜΛΑ-03 απαγορεύει.
//
// Ο **κύριος είναι ο διακομιστής**, γιατί εκεί ζει η άρνηση: η αναφορά σε μη
// μέλος απορρίπτεται με 422 από το API, όχι από την οθόνη. Ίδιος κανόνας με τη
// **D33** — «ο κύριος και των δύο πλευρών είναι το σχήμα του API». Ο πελάτης
// εισάγει **αυτό** το αρχείο, και ο φράχτης του (`web/src/services/mentions.test.js`)
// επαληθεύει ότι δεν γράφτηκε δεύτερο.

/**
 * Οι χαρακτήρες που επιτρέπονται σε όνομα χρήστη, και τα όριά του.
 *
 * ⚠ **Είναι ΑΝΤΙΓΡΑΦΟ του σχήματος του χρήστη, και το γνωρίζει.** Ο κανόνας ζει
 * ήδη σε δύο σημεία — στο μοντέλο και στο σχήμα εγγραφής — και τρίτη διατύπωση
 * που θα αποκλίνει σιωπηλά είναι ακριβώς η υπογραφή που έχει κοστίσει σε αυτή την
 * εργασία πέντε φορές. Γι' αυτό η ταύτιση **δεν δηλώνεται: ελέγχεται**. Η
 * `tests/mentions.test.js` διαβάζει το σχήμα του μοντέλου τη στιγμή που τρέχει
 * και αντιπαραβάλλει και τα τρία μεγέθη.
 *
 * Η αντιγραφή είναι αναπόφευκτη επειδή το άρθρωμα πρέπει να μείνει **καθαρό**:
 * εισαγωγή του μοντέλου εδώ θα έσερνε ολόκληρη τη Mongoose μέσα στο πακέτο της
 * διαδικτυακής εφαρμογής.
 */
export const USERNAME_CHARS = 'A-Za-z0-9._-';
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 32;

/**
 * Οι χαρακτήρες που **δεν** μπορούν να τελειώνουν μια αναφορά.
 *
 * Η τελεία, η παύλα και η κάτω παύλα είναι **έγκυροι** χαρακτήρες ονόματος, οπότε
 * χωρίς αποκοπή η φράση «ρώτησε τον @annamaria.» παράγει υποψήφιο «annamaria.»,
 * που δεν αντιστοιχεί σε κανέναν. Το αποτέλεσμα θα ήταν ότι **κάθε αναφορά στο
 * τέλος πρότασης απορρίπτεται** — δηλαδή η συνηθισμένη περίπτωση.
 */
const TRAILING = /[._-]+$/;

/**
 * Ο πυρήνας: `@` που **δεν συνεχίζει λέξη**, και μετά χαρακτήρες ονόματος.
 *
 * Η πρώτη ομάδα είναι ο χαρακτήρας **πριν** από το `@` και δεν είναι διακοσμητική:
 * χωρίς αυτήν, η διεύθυνση `hello@enhance.gr` μέσα σε ένα σχόλιο παράγει υποψήφιο
 * «enhance.gr». Οι διευθύνσεις ηλεκτρονικού ταχυδρομείου μέσα σε σχόλια δεν είναι
 * ακραία περίπτωση — είναι ο συνηθισμένος τρόπος να δώσει κανείς επαφή.
 */
const MENTION = new RegExp(`(^|[^${USERNAME_CHARS}])@([${USERNAME_CHARS}]+)`, 'g');

/** Το ίδιο, αγκυρωμένο στο **τέλος**: η αναφορά που πληκτρολογείται τώρα. */
const TYPING = new RegExp(`(^|[^${USERNAME_CHARS}])@([${USERNAME_CHARS}]*)$`);

/**
 * Οι αναφορές ενός κειμένου, **με τις θέσεις τους**.
 *
 * Οι θέσεις υπάρχουν επειδή τις χρειάζεται η απόδοση: η επισήμανση σπάει το κείμενο
 * σε τμήματα και δεν επιτρέπεται να το κάνει με δεύτερη αναζήτηση. Δεύτερη
 * αναζήτηση σημαίνει δεύτερος κανόνας.
 *
 * @param {string} text
 * @returns {Array<{ username: string, start: number, end: number, raw: string }>}
 *   `username` πεζό (το όνομα χρήστη είναι πεζό και μοναδικό)· `raw` όπως γράφτηκε,
 *   ώστε η οθόνη να δείχνει ό,τι πληκτρολόγησε ο άνθρωπος.
 */
export function findMentions(text) {
  const source = typeof text === 'string' ? text : '';
  const found = [];

  for (const match of source.matchAll(MENTION)) {
    const prefix = match[1] ?? '';
    // Η θέση του `@`, όχι της αντιστοίχισης: η πρώτη ομάδα καταναλώνει έναν
    // χαρακτήρα που **δεν** ανήκει στην αναφορά.
    const start = match.index + prefix.length;
    const written = match[2];
    const trimmed = written.replace(TRAILING, '');

    // ⚠ **Το μήκος κρίνεται ΜΕΤΑ την αποκοπή.** Το «@ab.» έχει τρεις χαρακτήρες
    // γραμμένους και δύο ονόματος: αν κριθεί πριν, γίνεται δεκτό και μετά ψάχνεται
    // όνομα δύο χαρακτήρων που το σχήμα δεν επιτρέπει να υπάρχει.
    if (trimmed.length < USERNAME_MIN || trimmed.length > USERNAME_MAX) continue;

    found.push({
      username: trimmed.toLowerCase(),
      start,
      // `1` για το ίδιο το `@`.
      end: start + 1 + trimmed.length,
      raw: `@${trimmed}`
    });
  }

  return found;
}

/**
 * Τα ονόματα που αναφέρει το κείμενο: πεζά, **χωρίς διπλότυπα**, με σταθερή σειρά
 * εμφάνισης.
 *
 * Χωρίς την αφαίρεση διπλοτύπων, δύο αναφορές του ίδιου προσώπου μέσα στο ίδιο
 * σχόλιο θα έδιναν δύο παραλήπτες — που η `resolveRecipients` θα ένωνε ούτως ή
 * άλλως, αλλά το **μήνυμα άρνησης** θα ονόμαζε το ίδιο όνομα δύο φορές.
 */
export function mentionedUsernames(text) {
  return [...new Set(findMentions(text).map((mention) => mention.username))];
}

/**
 * Η αναφορά που **πληκτρολογείται τώρα**, αν υπάρχει.
 *
 * Καλείται από τον επιλογέα ονομάτων της διεπαφής, και είναι ο λόγος που ο
 * κανόνας πρέπει να είναι ένας: ο επιλογέας οφείλει να ανοίγει **ακριβώς όταν** ο
 * διακομιστής θα αναγνωρίσει αναφορά. Αν ανοίγει νωρίτερα, προσφέρει συμπλήρωση
 * που δεν θα μετρήσει· αν αργότερα, ο χρήστης πληκτρολογεί ολόκληρο το όνομα με
 * το χέρι και ο επιλογέας είναι διακοσμητικός.
 *
 * **Το ελάχιστο μήκος ΔΕΝ ισχύει εδώ, και είναι σκόπιμο:** ο κατάλογος ανοίγει με
 * το `@` σκέτο, πριν πληκτρολογηθεί οτιδήποτε. Ένας επιλογέας που εμφανίζεται
 * μετά τον τρίτο χαρακτήρα δεν βοηθά όποιον δεν θυμάται το όνομα — που είναι
 * ολόκληρος ο λόγος ύπαρξής του.
 *
 * @param {string} text  Το πλήρες περιεχόμενο του πεδίου.
 * @param {number} caret Η θέση του δρομέα.
 * @returns {{ start: number, end: number, query: string } | null}
 */
export function activeMentionAt(text, caret) {
  const source = typeof text === 'string' ? text : '';
  const at = Number.isInteger(caret) ? Math.max(0, Math.min(caret, source.length)) : source.length;

  const match = TYPING.exec(source.slice(0, at));
  if (!match) return null;

  const written = match[2];
  // Πέρα από το ανώτατο μήκος ονόματος δεν πρόκειται για αναφορά: είναι λέξη που
  // τυχαίνει να αρχίζει με `@`. Ο κατάλογος **κλείνει** αντί να δείχνει «καμία
  // αντιστοιχία» επ' άπειρον.
  if (written.length > USERNAME_MAX) return null;

  return {
    start: at - written.length - 1,
    end: at,
    query: written.toLowerCase()
  };
}

/**
 * Το κείμενο σπασμένο σε τμήματα, για απόδοση.
 *
 * Επιστρέφει εναλλάξ απλό κείμενο και αναφορές, ώστε η οθόνη να μη χρειάζεται να
 * ξαναβρεί τις θέσεις. **Η ίδια συνάρτηση χρησιμοποιείται σε κάθε σημείο που
 * αποδίδει κείμενο συνομιλίας** — φυσαλίδα σχολίου, σημείωση προόδου,
 * αιτιολόγηση εκτίμησης — γιατί τρία αντίγραφα σήμανσης αποκλίνουν στο πρώτο
 * δεύτερο σημείο χρήσης (το μάθημα του `CommentBubble`).
 *
 * @param {string} text
 * @returns {Array<{ type: 'text'|'mention', value: string, username?: string }>}
 */
export function splitByMentions(text) {
  const source = typeof text === 'string' ? text : '';
  const mentions = findMentions(source);
  const parts = [];
  let cursor = 0;

  for (const mention of mentions) {
    if (mention.start > cursor) {
      parts.push({ type: 'text', value: source.slice(cursor, mention.start) });
    }
    parts.push({ type: 'mention', value: mention.raw, username: mention.username });
    cursor = mention.end;
  }

  // Το υπόλοιπο μπαίνει **μόνο αν υπάρχει**: κενό τμήμα κειμένου θα αποδιδόταν ως
  // κόμβος χωρίς περιεχόμενο, και θα έκανε το πλήθος των τμημάτων να εξαρτάται από
  // το αν η τελευταία λέξη ήταν αναφορά — μέγεθος που καμία οθόνη δεν θέλει.
  if (cursor < source.length) parts.push({ type: 'text', value: source.slice(cursor) });

  return parts;
}

export default { findMentions, mentionedUsernames, activeMentionAt, splitByMentions };
